import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/Icon';
import AdUserPicker from '@/components/AdUserPicker';
import AdUserCreateModal from '@/components/AdUserCreateModal';
import OnecEmployeePicker from '@/components/OnecEmployeePicker';
import SkudPersonPicker from '@/components/SkudPersonPicker';
import SkudPassesBlock from '@/components/SkudPassesBlock';
import ImpersonateLinkBlock from '@/components/ImpersonateLinkBlock';
import { ApiError, api } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import ExchangeMailboxRow from './ExchangeMailboxRow';

const ACTION_LABELS = {
  read:    'Чтение',
  write:   'Запись',
  delete:  'Удаление',
  trigger: 'Запуск',
  control: 'Управление',
};

const EMPTY = {
  name: '',
  role: 'teacher',
  email: '',
  extPhone: '',
  adUserPrincipalName: '',
  adDn: '',
  active: true,
  canTeach: false,
  isSuperAdmin: false,
  password: '',
  passwordConfirm: '',
  permissions: {},
  onecEmployeeId: null,
  onecEmployee: null,   // { id, code, name } для отображения текущей привязки
  skudPersonId: null,
  skudPerson: null,     // { skudId, lastName, firstName, ... } для отображения
};

/**
 *   mode='create' staff=null    → POST /api/staff
 *   mode='edit'   staff={...}   → PUT /api/staff/{id}
 *
 * Способ входа выбирается чекбоксами:
 *   • Локальный (email + пароль)
 *   • Active Directory (UPN)
 *   Можно оба, можно ни одного — тогда сотрудник просто числится в списке.
 */
export default function StaffEditModal({ mode, staff, onClose, onSaved, onDeleted }) {
  const isEdit = mode === 'edit';
  const { user: currentUser } = useAuth();
  const currentIsSuper = !!currentUser?.isSuperAdmin;

  const [data, setData] = useState(() => {
    if (!isEdit) return EMPTY;
    return {
      name:                staff.name || '',
      role:                staff.role || 'teacher',
      email:               staff.email || '',
      extPhone:            staff.extPhone || '',
      adUserPrincipalName: staff.adUserPrincipalName || '',
      adDn:                staff.adDn || '',
      active:              !!staff.active,
      canTeach:            !!staff.canTeach,
      isSuperAdmin:        !!staff.isSuperAdmin,
      password:            '',
      passwordConfirm:     '',
      permissions:         staff.permissions || {},
      onecEmployeeId:      staff.onecEmployee?.id ?? null,
      onecEmployee:        staff.onecEmployee || null,
      skudPersonId:        staff.skudPerson?.skudId ?? null,
      skudPerson:          staff.skudPerson || null,
    };
  });

  const [registry, setRegistry] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [refsBlock, setRefsBlock] = useState(null);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [newPwd, setNewPwd]     = useState('');
  const [creatingAd, setCreatingAd] = useState(false);

  // Пресеты прав — короткий список для выпадающего «Применить пресет».
  // Тянем тихо: если у текущего пользователя нет права читать пресеты —
  // массив останется пустым, кнопка просто не появится.
  const [presets, setPresets] = useState([]);
  // id применённого пресета — для возможной подсказки «применён …»; не
  // сохраняется на бэке, просто визуальный маркер сессии.
  const [appliedPresetId, setAppliedPresetId] = useState('');

  useEffect(() => {
    api.get('/api/staff/permissions-registry')
      .then((res) => setRegistry(res.registry || {}))
      .catch(() => setRegistry({}));
    api.get('/api/permission-presets')
      .then((res) => setPresets(res.rows || []))
      .catch(() => setPresets([]));
  }, []);

  // Применить пресет — копирует права в форму. Сам апдейт пользователя
  // произойдёт по «Сохранить»; так оператор может довести вручную.
  const applyPreset = async (presetId) => {
    if (!presetId) return;
    setAppliedPresetId(String(presetId));
    try {
      const res = await api.get('/api/permission-presets/' + presetId);
      const p = res.preset || {};
      setData((d) => ({
        ...d,
        isSuperAdmin: !!p.isSuperAdmin,
        permissions:  p.permissions || {},
      }));
    } catch (e) {
      // Не палим — оставим как было; ошибку покажем общим баннером.
      setError(e instanceof ApiError ? e.message : 'Не удалось применить пресет');
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Способы входа — отдельные стейты, чтобы UI был интуитивный
  const [authLocal, setAuthLocal] = useState(() => isEdit ? !!staff.email : false);
  const [authAd,    setAuthAd]    = useState(() => isEdit ? !!staff.adUserPrincipalName : false);

  const canTogglePerm = data.role === 'administrator';

  const togglePerm = (key, action) => {
    setData((d) => {
      const cur = d.permissions[key] || [];
      const has = cur.includes(action);
      const next = has ? cur.filter((a) => a !== action) : [...cur, action];
      const newMap = { ...d.permissions };
      if (next.length) newMap[key] = next;
      else delete newMap[key];
      return { ...d, permissions: newMap };
    });
  };

  const groupedRegistry = useMemo(() => {
    if (!registry) return [];
    const byGroup = new Map();
    for (const [key, meta] of Object.entries(registry)) {
      if (!byGroup.has(meta.group)) byGroup.set(meta.group, []);
      byGroup.get(meta.group).push({ key, ...meta });
    }
    return Array.from(byGroup.entries()).map(([group, items]) => ({ group, items }));
  }, [registry]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null); setRefsBlock(null);

    if (authLocal && !data.email.trim()) {
      setError('Email обязателен при локальном входе');
      setSaving(false);
      return;
    }
    if (!isEdit && authLocal) {
      if (!data.password) { setError('Введите пароль'); setSaving(false); return; }
      if (data.password.length < 8) { setError('Пароль должен быть не короче 8 символов'); setSaving(false); return; }
      if (data.password !== data.passwordConfirm) { setError('Пароли не совпадают'); setSaving(false); return; }
    }
    if (authAd && !data.adUserPrincipalName.trim()) {
      setError('Укажите AD User Principal Name (логин в AD)');
      setSaving(false);
      return;
    }

    const payload = {
      name: data.name.trim(),
      role: data.role,
      active: data.active,
      canTeach: data.canTeach,
      email: authLocal ? data.email.trim() : null,
      extPhone: data.extPhone.trim(),
      adUserPrincipalName: authAd ? data.adUserPrincipalName.trim() : null,
      adDn: authAd ? (data.adDn.trim() || null) : null,
      permissions: data.role === 'administrator' ? data.permissions : null,
      onecEmployeeId: data.onecEmployeeId ?? null,
      skudPersonId:   data.skudPersonId ?? null,
    };
    // Передаём флаг суперадмина только если текущий юзер сам суперадмин —
    // иначе бэк всё равно его игнорирует, лишний шум в payload не нужен.
    if (currentIsSuper) {
      payload.isSuperAdmin = data.role === 'administrator' && !!data.isSuperAdmin;
    }
    if (!isEdit && authLocal) payload.password = data.password;

    try {
      const res = isEdit
        ? await api.put(`/api/staff/${staff.id}`, payload)
        : await api.post('/api/staff', payload);
      onSaved?.(res.staff);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'in_use' && err.payload?.error?.references) {
        setRefsBlock(err.payload.error);
      } else {
        setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
      }
    } finally { setSaving(false); }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!confirm(`Удалить сотрудника «${data.name}»?\nДействие необратимо.`)) return;
    setSaving(true); setError(null); setRefsBlock(null);
    try {
      await api.delete(`/api/staff/${staff.id}`);
      onDeleted?.(staff.id);
      onClose();
    } catch (err) {
      // in_use — активные назначения (классрук/ассистент, нужно переназначить).
      // has_history — историческая активность (оценки, проведённые занятия,
      // зарплаты), удалять нельзя; UI подсказывает архивировать.
      const isRefErr = err instanceof ApiError
        && (err.code === 'in_use' || err.code === 'has_history')
        && err.payload?.error?.references;
      if (isRefErr) {
        setRefsBlock(err.payload.error);
      } else {
        setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось удалить'));
      }
      setSaving(false);
    }
  };

  const onResetPassword = async () => {
    if (!isEdit) return;
    if (newPwd.length < 8) { setError('Пароль должен быть не короче 8 символов'); return; }
    setSaving(true); setError(null);
    try {
      await api.post(`/api/staff/${staff.id}/password`, { password: newPwd });
      alert(staff?.hasPassword ? 'Пароль обновлён' : 'Пароль задан');
      setShowResetPwd(false); setNewPwd('');
      // Просим родителя перезагрузить карточку сотрудника — чтобы
      // hasPassword обновилось и кнопка переименовалась из «Задать»
      // в «Сменить».
      onSaved?.({ ...staff, hasPassword: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось'));
    } finally { setSaving(false); }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay')) onClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isEdit ? `Сотрудник — ${data.name || '...'}` : 'Новый сотрудник'}</h2>
          <button className="sd-close" onClick={onClose}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">ФИО *</label>
              <input
                className="sd-edit__input" required autoFocus
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
              />
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Роль *</label>
              <select
                className="sd-edit__select"
                value={data.role}
                onChange={(e) => set('role', e.target.value)}
              >
                <option value="administrator">Администратор</option>
                <option value="teacher">Учитель</option>
                <option value="employee">Сотрудник (без доступа в CRM)</option>
              </select>
              {data.role === 'employee' && (
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 4 }}>
                  Учётка нужна только для AD и пропуска СКУД. В CRM этот
                  человек не входит — пароля и прав у него нет.
                </div>
              )}
              {data.role === 'administrator' && (
                <>
                  <label className="staff-auth-row" style={{ marginTop: 8 }}>
                    <input
                      type="checkbox"
                      checked={data.canTeach}
                      onChange={(e) => set('canTeach', e.target.checked)}
                    />
                    <span>Показывать в списке учителей</span>
                  </label>
                  <label
                    className="staff-auth-row"
                    style={{ marginTop: 6 }}
                    title={currentIsSuper
                      ? 'Полный доступ ко всем разделам, в том числе настройкам безопасности'
                      : 'Менять этот флаг может только действующий суперадминистратор'}
                  >
                    <input
                      type="checkbox"
                      checked={data.isSuperAdmin}
                      onChange={(e) => set('isSuperAdmin', e.target.checked)}
                      disabled={!currentIsSuper}
                    />
                    <span style={{ color: data.isSuperAdmin ? 'var(--green-700)' : undefined, fontWeight: data.isSuperAdmin ? 600 : 400 }}>
                      Суперадминистратор
                      {!currentIsSuper && (
                        <span style={{ color: 'var(--fg-faint)', marginLeft: 6, fontWeight: 400, fontSize: 11 }}>
                          (только для другого суперадмина)
                        </span>
                      )}
                    </span>
                  </label>
                </>
              )}
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">Статус</label>
              <select
                className="sd-edit__select"
                value={data.active ? '1' : '0'}
                onChange={(e) => set('active', e.target.value === '1')}
              >
                <option value="1">Активен</option>
                <option value="0">В архиве</option>
              </select>
            </div>

            <div className="sd-edit__field">
              <label className="sd-edit__label">
                Внутренний номер на АТС
              </label>
              <input
                className="sd-edit__input"
                type="text"
                inputMode="numeric"
                placeholder="например, 1011"
                value={data.extPhone}
                onChange={(e) => set('extPhone', e.target.value)}
              />
              <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 4 }}>
                Если задан — сотрудник сможет звонить «через АТС» из карточки ученика.
              </div>
            </div>

            {/* === Способы входа === У employee локального входа нет
                (пароль не разрешён, в CRM не пускаем), но AD-привязка
                остаётся — она нужна для учётки в домене / почты. */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label" style={{ marginBottom: 8 }}>
                {data.role === 'employee' ? 'AD и учётки' : 'Способы входа в CRM'}
              </label>

              {data.role !== 'employee' && (
                <label className="staff-auth-row">
                  <input type="checkbox" checked={authLocal} onChange={(e) => setAuthLocal(e.target.checked)} />
                  <span>Локально (email + пароль)</span>
                </label>
              )}
              {authLocal && (
                <div className="staff-auth-fields">
                  <div className="sd-edit__field sd-edit__field--full">
                    <label className="sd-edit__label">Email *</label>
                    <input
                      className="sd-edit__input"
                      type="email"
                      value={data.email}
                      onChange={(e) => set('email', e.target.value)}
                      required={authLocal}
                    />
                  </div>
                  {!isEdit && (
                    <>
                      <div className="sd-edit__field">
                        <label className="sd-edit__label">Пароль *</label>
                        <input
                          className="sd-edit__input"
                          type="password"
                          value={data.password}
                          onChange={(e) => set('password', e.target.value)}
                        />
                      </div>
                      <div className="sd-edit__field">
                        <label className="sd-edit__label">Повторите пароль *</label>
                        <input
                          className="sd-edit__input"
                          type="password"
                          value={data.passwordConfirm}
                          onChange={(e) => set('passwordConfirm', e.target.value)}
                        />
                      </div>
                    </>
                  )}
                  {isEdit && (
                    <div className="sd-edit__field sd-edit__field--full" style={{ alignSelf: 'start' }}>
                      <button type="button" className="btn btn--sm" onClick={() => setShowResetPwd((v) => !v)}>
                        {staff?.hasPassword ? 'Сменить пароль' : 'Задать пароль'}
                      </button>
                      {!staff?.hasPassword && (
                        <div style={{ fontSize: 11.5, color: 'var(--fg-muted)', marginTop: 4 }}>
                          У сотрудника ещё нет локального пароля — задайте, чтобы он мог войти через email.
                        </div>
                      )}
                      {showResetPwd && (
                        <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center' }}>
                          <input
                            className="sd-edit__input"
                            type="password"
                            placeholder="Новый пароль (≥8)"
                            value={newPwd}
                            onChange={(e) => setNewPwd(e.target.value)}
                            style={{ maxWidth: 260 }}
                          />
                          <button type="button" className="btn btn--primary btn--sm" onClick={onResetPassword} disabled={saving}>
                            Применить
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <label className="staff-auth-row" style={{ marginTop: 6 }}>
                <input type="checkbox" checked={authAd} onChange={(e) => setAuthAd(e.target.checked)} />
                <span>Active Directory</span>
              </label>
              {authAd && (
                <div className="staff-auth-fields">
                  <div className="sd-edit__field sd-edit__field--full">
                    <label className="sd-edit__label">Пользователь AD *</label>
                    <AdUserPicker
                      value={data.adUserPrincipalName ? {
                        upn: data.adUserPrincipalName,
                        dn:  data.adDn,
                        displayName: data.name,
                      } : null}
                      defaultName={data.name}
                      onChange={(u) => {
                        if (u) {
                          set('adUserPrincipalName', u.upn || '');
                          set('adDn', u.dn || '');
                          if (!data.name && u.displayName) set('name', u.displayName);
                        } else {
                          set('adUserPrincipalName', '');
                          set('adDn', '');
                        }
                      }}
                    />
                    <div className="sd-edit__hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span>Выберите существующего пользователя AD или</span>
                      <button
                        type="button"
                        className="btn btn--sm"
                        onClick={() => setCreatingAd(true)}
                        style={{ padding: '2px 8px', height: 22, fontSize: 12 }}
                      >
                        <Icon name="plus" size={11} /> Создать нового
                      </button>
                    </div>
                    {/* Статус почтового ящика Exchange — показывается, когда UPN
                     * выбран и интеграция включена. Прячется сама, если её нет. */}
                    <ExchangeMailboxRow upn={data.adUserPrincipalName} />
                  </div>
                </div>
              )}

              {!authLocal && !authAd && data.role !== 'employee' && (
                <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginTop: 6 }}>
                  Без входа в CRM — сотрудник числится в списке (например, для СКУД-пропусков).
                </div>
              )}
            </div>

            {data.role === 'teacher' && (
              <div className="sd-edit__field sd-edit__field--full">
                <div className="staff-info-block">
                  <Icon name="info" size={13} />
                  <div>
                    Учитель видит только свои уроки. Если он назначен классруком/ассистентом —
                    дополнительно может просматривать (не редактировать) занятия своего класса.
                    Эти правила фиксированы и не настраиваются.
                  </div>
                </div>
              </div>
            )}

            {/* === Сотрудник в 1С ЗУП — для зарплат за доп. занятия. ===
                Показываем для учителей и для администраторов с canTeach=true,
                т.е. для тех, кто реально может вести занятия. */}
            {(data.role === 'teacher' || data.canTeach) && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Сотрудник 1С ЗУП</label>
                <OnecEmployeePicker
                  value={data.onecEmployee || (data.onecEmployeeId ? { id: data.onecEmployeeId } : null)}
                  defaultName={data.name}
                  onChange={(emp) => {
                    set('onecEmployee',   emp);
                    set('onecEmployeeId', emp?.id ?? null);
                  }}
                />
                <div className="sd-edit__hint">
                  Привязка нужна для выгрузки зарплат за доп. занятия. Если сотрудника
                  ещё нет в списке — заведите его в ЗУП и нажмите «Обновить» в выпадающем меню.
                </div>
              </div>
            )}

            {/* === Человек в СКУД — нужен, чтобы при деактивации
                автоматически блокировались его пропуска. === */}
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Человек в СКУД</label>
              <SkudPersonPicker
                value={data.skudPerson || (data.skudPersonId
                  ? { skudId: data.skudPersonId, lastName: '#' + data.skudPersonId, firstName: '', middleName: '' }
                  : null)}
                role="employee"
                onChange={(p) => {
                  set('skudPerson',   p);
                  set('skudPersonId', p?.skudId ?? null);
                }}
              />
              <div className="sd-edit__hint">
                Список фильтруется по подразделению «Сотрудники» из настроек СКУД.
                При деактивации учётки все пропуска этого человека будут
                автоматически заблокированы.
              </div>
            </div>

            {/* Если СКУД-человек уже выбран — показываем его пропуска. */}
            {data.skudPersonId && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label">Пропуска СКУД</label>
                <SkudPassesBlock skudPersonId={data.skudPersonId} />
              </div>
            )}

            {/* «Войти как» — отладка кабинета глазами учителя. Только для
                роли teacher (под админов бэкенд входить не даёт) и только
                в edit-режиме; право проверяется внутри блока. */}
            {isEdit && data.role === 'teacher' && (
              <ImpersonateLinkBlock userId={staff.id} />
            )}

            {/* === Матрица прав — только для администраторов === */}
            {data.role === 'administrator' && (
              <div className="sd-edit__field sd-edit__field--full">
                <label className="sd-edit__label" style={{ marginBottom: 8 }}>
                  Права администратора {!canTogglePerm && <span style={{ color: 'var(--fg-muted)' }}>(только для админа)</span>}
                </label>

                {/* Применить пресет — копирует isSuperAdmin + permissions
                    из выбранного пресета в форму. Видно, только если
                    есть хоть один пресет (и есть право его читать). */}
                {presets.length > 0 && canTogglePerm && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 12.5, color: 'var(--fg-muted)' }}>Применить пресет:</span>
                    <select
                      value={appliedPresetId}
                      onChange={(e) => applyPreset(e.target.value)}
                      style={{
                        height: 28, padding: '0 8px',
                        border: '1px solid var(--border-strong)', borderRadius: 6,
                        fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
                        minWidth: 220,
                      }}
                    >
                      <option value="">— выбрать —</option>
                      {presets.map((p) => (
                        <option key={p.id} value={p.id} title={p.description || undefined}>
                          {p.name}{p.isSuperAdmin ? ' · super' : ''}
                        </option>
                      ))}
                    </select>
                    {appliedPresetId && (
                      <span style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
                        Применён. Можно довести вручную и нажать «Сохранить».
                      </span>
                    )}
                  </div>
                )}
                {!registry ? (
                  <div style={{ color: 'var(--fg-muted)' }}>Загружаем реестр прав…</div>
                ) : (
                  <div className="staff-perms">
                    {groupedRegistry.map(({ group, items }) => (
                      <div key={group} className="staff-perms__group">
                        <div className="staff-perms__group-title">{group}</div>
                        <table className="staff-perms__table">
                          <tbody>
                            {items.map((it) => (
                              <tr key={it.key}>
                                <td className="staff-perms__label">{it.label}</td>
                                {it.actions.map((a) => (
                                  <td key={a} className="staff-perms__cell">
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={(data.permissions[it.key] || []).includes(a)}
                                        onChange={() => togglePerm(it.key, a)}
                                      />
                                      <span>{ACTION_LABELS[a] || a}</span>
                                    </label>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {refsBlock && (
            <div className="sd-edit__error" style={{ margin: '0 20px 12px' }}>
              <strong>{refsBlock.message}</strong>
              {Array.isArray(refsBlock.references) && refsBlock.references.length > 0 && (
                <ul style={{ margin: '6px 0 0 18px' }}>
                  {refsBlock.references.map((r, i) => (<li key={i}>{r.label}</li>))}
                </ul>
              )}
            </div>
          )}

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            {isEdit && (
              <button type="button" className="btn btn--sm btn--danger" onClick={onDelete} disabled={saving}>
                Удалить
              </button>
            )}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (isEdit ? 'Сохранить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>

      {creatingAd && (
        <AdUserCreateModal
          initial={{ name: data.name, email: data.email }}
          onClose={() => setCreatingAd(false)}
          onCreated={(u) => {
            set('adUserPrincipalName', u.upn || '');
            set('adDn', u.dn || '');
            if (!data.name && u.displayName) set('name', u.displayName);
          }}
        />
      )}
    </div>
  );
}

